import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';
import uuid from 'uuid';

import storeLoader from 'app/stores/storeLoader';
import { formConsumer, Field, Select } from 'core/form';
import { NOTIFICATION_BROADCAST_CHANNEL_ID } from '@kentik/ui-shared/notifications/constants';
import { Box, showErrorToast, showSuccessToast, Spinner, Suspense } from 'core/components';
import Button from 'core/components/Button';

import NotificationModel from 'app/stores/notifications/NotificationModel';
import NotificationOption from './NotificationOption';

const Container = styled.div`
  .${Classes.INPUT} > .${Classes.TAG} {
    padding-left: 0px;
  }
`;

@formConsumer
@storeLoader('$notifications')
@inject('$auth')
@observer
class NotificationsSelect extends Component {
  state = {
    loading: false
  };

  static defaultProps = {
    fieldName: 'notifications',
    showTestButton: true,
    isRequired: false,
    fieldProps: {},
    containerProps: {},
    hideTenantChannels: true
  };

  bc = null;

  pendingRequestId = null;

  pendingTimeout = null;

  componentWillUnmount() {
    this.cleanupBroadcast();
  }

  cleanupBroadcast() {
    if (this.pendingTimeout) {
      window.clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }

    if (this.bc) {
      this.bc.close();
      this.bc = null;
    }

    this.pendingRequestId = null;
  }

  get canAddNotificationChannel() {
    const { $auth } = this.props;
    return $auth.hasRbacPermissions(['alerting.notifications::create']);
  }

  get options() {
    const { $notifications, hideTenantChannels } = this.props;
    const options = $notifications.collection.notificationOptions;

    if (hideTenantChannels) {
      return options.filter((option) => !parseInt(option.userGroupId, 10) > 0);
    }

    return options;
  }

  get field() {
    const { form, fieldName, fieldProps } = this.props;
    return fieldProps.field || form.getField(fieldName);
  }

  optionFormatter = (item) =>
    item && (
      <Box>
        <NotificationOption option={item} />
      </Box>
    );

  handleAddClick = () => {
    const requestId = uuid.v4();

    // clear any old listener
    this.cleanupBroadcast();

    this.pendingRequestId = requestId;

    this.bc = new BroadcastChannel(NOTIFICATION_BROADCAST_CHANNEL_ID);
    this.bc.onmessage = (event) => {
      const msg = event && event.data;

      if (!msg || msg.requestId !== this.pendingRequestId) {
        return;
      }

      if (msg.type === 'notification-created') {
        const { $notifications } = this.props;

        const newModel = new NotificationModel();
        newModel
          .set({ channelID: msg.channelId })
          .fetch()
          .then(() => {
            $notifications.collection.add(newModel);

            const currentSelections = this.field.value || [];

            if (!currentSelections.includes(msg.channelId)) {
              this.field.onChange(currentSelections.concat(msg.channelId));
            }

            this.cleanupBroadcast();
          })
          .catch(() => {
            showErrorToast('Failed to fetch the new notification. Please try again.');
            this.cleanupBroadcast();
          });

        return;
      }

      if (msg.type === 'notification-cancelled') {
        this.cleanupBroadcast();
      }
    };

    // stop listening after 10 minutes
    this.pendingTimeout = window.setTimeout(
      () => {
        this.cleanupBroadcast();
      },
      10 * 60 * 1000
    );

    const url = `/v4/settings/notifications/new?requestId=${encodeURIComponent(requestId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  handleTestClick = () => {
    const { $notifications } = this.props;

    this.setState({ loading: true });

    Promise.all(this.field.value.map((chId) => $notifications.testChannelById(chId)))
      .then(
        () => {
          showSuccessToast('Test Notifications have been sent.');
        },
        () => {
          showErrorToast('Test Notifications could not be sent.');
        }
      )
      .then(() => {
        this.setState({ loading: false });
      });
  };

  render() {
    const { loading } = this.state;
    const {
      $auth,
      form,
      $notifications,
      fieldName,
      disabled,
      showAddButton,
      showTestButton,
      hideTenantChannels,
      containerProps,
      fieldProps,
      onChange,
      ...selectProps
    } = this.props;

    return (
      <Suspense
        loading={!$notifications.collection.hasFetched && $notifications.collection.loading}
        fallback={
          <Box pt={2}>
            <Spinner size={14} />
          </Box>
        }
      >
        <Container {...containerProps}>
          <Field
            name={fieldName}
            options={this.options}
            width="auto"
            mb="4px"
            large
            disabled={disabled}
            onChange={onChange}
            {...fieldProps}
          >
            <Select
              fill
              multi
              showFilter
              optionFormatter={this.optionFormatter}
              valueRenderer={this.optionFormatter}
              {...selectProps}
            />
          </Field>

          {this.canAddNotificationChannel && showAddButton && (
            <Button minimal text="Add New Channel" onClick={this.handleAddClick} intent="primary" />
          )}

          {this.canAddNotificationChannel && showTestButton && this.field.value.length > 0 && (
            <Button
              minimal
              intent="primary"
              text="Test Notification Channels"
              loading={loading}
              onClick={this.handleTestClick}
            />
          )}
        </Container>
      </Suspense>
    );
  }
}

export default NotificationsSelect;
